import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listLegalDocuments, createLegalDocument, deleteLegalDocument } from "@/integrations/backend/legal";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import LexicalHtmlEditor from "@/components/admin/LexicalHtmlEditor";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogBody, DialogDescription, DialogFooter, DialogHeader, DialogScrollContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreVertical, Edit3, Trash2 } from "lucide-react";

type LegalType = "terms" | "privacy" | "liability_waiver";

type LegalDoc = {
  id: string;
  type: LegalType;
  version: number;
  title: string;
  content: string;
  is_current: boolean;
  created_at: string;
  published_at: string;
};

const legalDocsQueryKey = queryKeys.legal.adminDocuments;

export function LegalView() {
  const queryClient = useQueryClient();
  const docsQuery = useQuery({
    queryKey: legalDocsQueryKey,
    queryFn: listLegalDocuments,
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...legalDocsQueryKey]),
  });
  const docs = (docsQuery.data ?? []) as unknown as LegalDoc[];
  const loading = docsQuery.isLoading;
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<LegalType>("terms");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [editingFrom, setEditingFrom] = useState<LegalDoc | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const invalidateLegalReads = async () => {
    await queryClient.invalidateQueries({ queryKey: legalDocsQueryKey });
    await queryClient.invalidateQueries({ queryKey: queryKeys.legal.status });
    await queryClient.invalidateQueries({ queryKey: ["legal", "current"] });
  };

  const nextVersion = useMemo(() => {
    const current = docs.filter(d => d.type === type).sort((a,b)=>b.version-a.version)[0]?.version ?? 0;
    return current + 1;
  }, [docs, type]);

  const createVersion = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    // Close immediately for better UX; save continues in the background.
    setOpen(false);
    try {
      await createLegalDocument({ type, version: nextVersion, title: title.trim(), content, is_current: true });
      setTitle("");
      setContent("");
      setEditingFrom(null);
    } catch (e) {
      // noop: keep UX simple
      // Keep draft state (title/content) so user can retry by re-opening.
    }
    setSaving(false);
    await invalidateLegalReads();
  };

  const openEdit = (d: LegalDoc) => {
    setEditingFrom(d);
    setType(d.type);
    setTitle(d.title);
    setContent(d.content);
    setOpen(true);
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this version?")) return;
    try { await deleteLegalDocument(id); } catch {}
    await invalidateLegalReads();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Legal Documents</h1>
          <p className="text-gray-600">Manage versioned Terms of Service, Privacy Policy, and Liability Waiver.</p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(v) => {
            // Don’t allow reopening/closing toggles while we’re saving.
            if (saving) return;
            setOpen(v);
            if (!v) setEditingFrom(null);
          }}
        >
          <DialogTrigger asChild>
            <Button disabled={saving}>{saving ? "Saving..." : "Create New Version"}</Button>
          </DialogTrigger>
          <DialogScrollContent className="sm:max-w-[1000px] bg-white text-black">
            <DialogHeader className="shrink-0">
              <DialogTitle>{editingFrom ? `Edit ${editingFrom.type} (save as new)` : "Create New Legal Version"}</DialogTitle>
              <DialogDescription>Publishing will mark this as the current version.</DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Type</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={type === "terms" ? "default" : "outline"}
                      size="sm"
                      onClick={()=>setType("terms")}
                      disabled={Boolean(editingFrom)}
                      className={type === "terms" ? "" : "text-black border-gray-300 hover:bg-gray-100"}
                    >
                      Terms
                    </Button>
                    <Button
                      variant={type === "privacy" ? "default" : "outline"}
                      size="sm"
                      onClick={()=>setType("privacy")}
                      disabled={Boolean(editingFrom)}
                      className={type === "privacy" ? "" : "text-black border-gray-300 hover:bg-gray-100"}
                    >
                      Privacy
                    </Button>
                    <Button
                      variant={type === "liability_waiver" ? "default" : "outline"}
                      size="sm"
                      onClick={()=>setType("liability_waiver")}
                      disabled={Boolean(editingFrom)}
                      className={type === "liability_waiver" ? "" : "text-black border-gray-300 hover:bg-gray-100"}
                    >
                      Liability Waiver
                    </Button>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Version</Label>
                  <Input value={nextVersion} readOnly />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Title</Label>
                <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder={type === "privacy" ? "e.g., Privacy Policy" : type === "liability_waiver" ? "e.g., Liability Waiver" : "e.g., Terms of Service"} />
              </div>
              <div className="space-y-1">
                <Label>Content</Label>
                <LexicalHtmlEditor
                  value={content}
                  onChange={setContent}
                  namespace="legal-lexical"
                  placeholder="Write the legal document…"
                  minHeightClassName="min-h-[320px]"
                />
              </div>
            </DialogBody>
            <DialogFooter className="shrink-0">
              <Button variant="outline" onClick={()=>setOpen(false)} className="text-black border-gray-300 hover:bg-gray-100">Cancel</Button>
              <Button onClick={createVersion} disabled={saving || !title.trim() || !content.trim()}>{saving ? "Saving..." : editingFrom ? "Save as New Version" : "Publish"}</Button>
            </DialogFooter>
          </DialogScrollContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Versions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table className="text-gray-900">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-gray-900">Type</TableHead>
                  <TableHead className="text-gray-900">Version</TableHead>
                  <TableHead className="text-gray-900">Title</TableHead>
                  <TableHead className="text-gray-900">Current</TableHead>
                  <TableHead className="w-[120px] text-gray-900">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!loading && docs.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="capitalize text-gray-900">{d.type}</TableCell>
                    <TableCell className="text-gray-900">{d.version}</TableCell>
                    <TableCell className="truncate max-w-[300px] text-gray-900">{d.title}</TableCell>
                    <TableCell className="text-gray-900">
                      {d.is_current ? (
                        <Badge className="bg-green-100 text-green-800">Current</Badge>
                      ) : (
                        <Badge variant="outline">Old</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-900">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" aria-label="Actions">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={()=>openEdit(d)}>
                            <Edit3 className="w-4 h-4 mr-2" /> Edit (save as new)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={()=>remove(d.id)} className="text-destructive focus:text-destructive">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {(!loading && docs.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-gray-500">No legal documents yet.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default LegalView;


